import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { chatbotService, ChatbotConfig } from '@/services/chatbotService';
import { generateStandardEmbedCode } from '../config/apiConfig';

interface UseChatbotConfigReturn {
  config: ChatbotConfig;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveConfig: () => Promise<boolean>;
  updateConfig: (updates: Partial<ChatbotConfig>) => void;
  isConfigValid: () => boolean;
  generateEmbedCode: (websiteUrl: string) => string;
  reloadConfig: () => void;
}

/**
 * Default chatbot configuration following UX best practices
 */
const DEFAULT_CONFIG: ChatbotConfig = {
  isEnabled: false,
  botName: "Wildlife Assistant",
  welcomeMessage: "Hello! I'm your wildlife assistant. How can I help you today?",
  primaryColor: "#2C5F2D", // Brand primary color
  secondaryColor: "#F3F4F6",
  position: "bottom-right",
  autoOpen: false,
  autoOpenDelay: 5,
  collectUserInfo: true,
  requireEmail: false,
  systemPrompt: "You are a helpful wildlife assistant specializing in wildlife tours, conservation, and animal education. You provide accurate information about wildlife experiences, safety guidelines, and booking procedures. Always be enthusiastic about wildlife conservation.",
  modelProvider: "huggingface",
  modelName: "mistralai/Mistral-7B-Instruct-v0.3"
};

/**
 * Custom hook for managing chatbot configuration
 * Handles state management, API calls, validation, and error handling
 */
export function useChatbotConfig(): UseChatbotConfigReturn {
  const [config, setConfig] = useState<ChatbotConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load configuration from server
   */
  const loadConfig = useCallback(async () => {
    console.log('Loading chatbot configuration...');
    setLoading(true);
    setError(null);
    
    try {
      const response = await chatbotService.getConfig();
      console.log('Chatbot configuration response:', response);
      
      if (response.success) {
        setConfig(response.data);
      } else if (response.error === 'Authentication required') {
        setError('Authentication required. Please refresh the page.');
        toast({
          title: "Session expired",
          description: "Please refresh the page to continue.",
          variant: "destructive",
        });
      } else {
        setError(response.error || 'Failed to load configuration');
        toast({
          title: "Error",
          description: "Failed to load chatbot configuration",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('Error loading chatbot configuration:', err);
      const errorMessage = 'Failed to load configuration';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save configuration to server
   */
  const saveConfig = useCallback(async (): Promise<boolean> => {
    setSaving(true);
    setError(null);
    
    try {
      // Ensure we're using Hugging Face as the default provider
      const configToSave = {
        ...config,
        modelProvider: "huggingface" as const,
      };
      
      const response = await chatbotService.updateConfig(configToSave);
      
      if (response.success) {
        setConfig(response.data);
        toast({
          title: "Success",
          description: "Chatbot configuration saved successfully",
        });
        return true;
      } else if (response.error === 'Authentication required') {
        setError('Authentication required. Please refresh the page.');
        toast({
          title: "Session expired",
          description: "Please refresh the page to continue.",
          variant: "destructive",
        });
      } else {
        setError(response.error || 'Failed to save configuration');
        toast({
          title: "Error",
          description: response.error || "Failed to save chatbot configuration",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('Error saving chatbot configuration:', err);
      const errorMessage = 'Failed to save configuration';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
    
    return false;
  }, [config]);

  /**
   * Update configuration state
   */
  const updateConfig = useCallback((updates: Partial<ChatbotConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Validate configuration completeness
   */
  const isConfigValid = useCallback((): boolean => {
    return Boolean(
      config.botName?.trim() &&
      config.welcomeMessage?.trim() &&
      config.systemPrompt?.trim() &&
      config.systemPrompt.length >= 50 &&
      config.primaryColor &&
      config.secondaryColor &&
      config.position &&
      config.modelProvider &&
      config.modelName
    );
  }, [config]);

  /**
   * Generate embed code for website integration
   */
  const generateEmbedCode = useCallback((websiteUrl: string): string => {
    return generateStandardEmbedCode({
      businessId: config.businessId || '',
      websiteUrl,
      botName: config.botName,
      welcomeMessage: config.welcomeMessage,
      primaryColor: config.primaryColor,
      position: config.position,
      autoOpen: config.autoOpen,
      autoOpenDelay: config.autoOpenDelay,
      collectUserInfo: config.collectUserInfo,
      requireEmail: config.requireEmail,
    });
  }, [config]);

  /**
   * Reload configuration from server
   */
  const reloadConfig = useCallback(() => {
    loadConfig();
  }, [loadConfig]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    loading,
    saving,
    error,
    saveConfig,
    updateConfig,
    isConfigValid,
    generateEmbedCode,
    reloadConfig,
  };
} 